import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { Button, ButtonGroup } from 'components/ui/button'
import { Fieldset, FieldSection } from 'components/ui/fieldset'
import { FooterEditEntity } from 'components/ui/footer'
import { Input } from 'components/ui/input'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SelectRef } from 'components/ui/select'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { MediaField } from 'domains/media/components/media-field'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'
import { spacesBindableActionCreators } from 'domains/spaces/actions'

import { AttributeRef } from 'generated/mos/entity'
import { ResolvedValueResult } from 'generated/mos/linkedfieldresolver'
import { Media } from 'generated/mos/media'
import { Page } from 'generated/mos/pagination'
import { Stop } from 'generated/mos/org/getty/audio'
import { GetStopRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateStopRequest } from 'generated/mos/org/getty/audiomanagement'
import { Space, Site } from 'generated/mos/structure'
import { Placement, PositionPlacement, SitePlacement, SpacePlacement } from 'generated/mos/placement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { areObjectsEqual, pick, areRefValuesEqual } from 'helpers/core'
import { getAlternateFieldValues, LinkableValue, getResolvedValue } from 'helpers/linkables'
import { convertEntityToFormValues, writeUsingSchema } from 'helpers/schema'
import { Status, statusSelector } from 'helpers/status'
import { baseDeepLinkDomain, gettyCenterSiteId, gettyVillaSiteId } from 'helpers/deep-links'

import { getValidationWarning } from 'domains/org/getty/utils/validation'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { actionCreators, GettyActionCreators } from '../../../actions'
import { GettyStore } from '../../../store'
import { SpacesStore } from '../../../../../spaces/store'
import { getStopSubnav } from '../subnav'
import { StopUpdateTours } from '../stop-update-tours/index'
import { StopDelete } from '../stop-delete'
import { stopSchema } from '../../../schemas/stop'
import { compareValueInequality } from './compareValueInequality'
import { TmsDetails } from 'domains/org/getty/components/tms-metadata'
import { appEnvironment, ENVIRONMENT } from "../../../utils/environment";

type FormValues = {
  title: string | undefined;
  artworkName: string | undefined;
  producedBy: string | undefined;
  placeCreated: string | undefined;
  dateCreated: string | undefined;
  materials: string | undefined;
  creditLine: string | undefined;
  imageAltText: string;
  imageCopyright: string | undefined;
  wallNumber: string;
  objectDescription: string | undefined;
  objectCopyright: string | undefined;
  placement: Space.Ref | undefined;
  publicImageTitle: string | undefined;
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetStopRequest' | 'gettyUpdateStopRequest' | 'gettyUpdateStopReset' | 'gettyListValidationFailuresRequest' | 'gettyListSitesRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof spacesBindableActionCreators, "spacesRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'stopUpdate' | 'sites' | 'validationFailuresList'> & Pick<SpacesStore["spaces"], "spacesList">


type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly stopId: string;
  readonly tourId?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

const makePlacementEntity = (spaceRef: Space.Ref): Placement.Entity => {
  return {
    ...Placement.defaults,
    placement: {
      ...SpacePlacement.defaults,
      spaceRef,
    },
  }
}

const getSpacePlacementSpaceRef = (placement: Placement.Entity): Space.Ref | undefined => {
  if (placement) {
    const { spaceRef } = placement.placement as SpacePlacement.Entity
    return Space.mustRef(spaceRef)
  }
  return undefined
}

// ref for the placeholder audio
const placeholderAudioRefId = (() => {
  switch (appEnvironment) {
    case ENVIRONMENT.PROD:
      return '0187218a-5066-03e2-0c26-fdfa95e6398f'
    case ENVIRONMENT.STAGING:
      return '0186aa45-05f5-dc0d-4904-a88f3df4ba81';
    case ENVIRONMENT.DEV:
      return '01880418-e584-1040-7f1c-64d003ccf689';
    case ENVIRONMENT.LOCAL:
    default:
      return '0186aa45-05f5-dc0d-4904-a88f3df4ba81';
  }
})()

export const StopUpdateView = (props: Props) => {
  const {
    gettyGetStopRequest,
    gettyListSitesRequest,
    gettyUpdateStopRequest,
    gettyUpdateStopReset,
    historyPush,
    projectId,
    stopId,
    stopUpdate,
    toastNotification,
    tourId,
    spacesList,
    spacesRequest,
    sites,
    validationFailuresList,
    gettyListValidationFailuresRequest,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
  } = props

  // setup form value variables.
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ imageMedia, setImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ audioMedia, setAudioMedia ] = useState<Media.Ref | undefined>(undefined)

  const hasSubmitted = useRef(false)

  // linkable override state.
  const [ titleOverride, setTitleOverride ] = useState<LinkableValue>(undefined)
  const [ artworkNameOverride, setArtworkNameOverride ] = useState<LinkableValue>(undefined)
  const [ producedByOverride, setProducedByOverride ] = useState<LinkableValue>(undefined)
  const [ placeCreatedOverride, setPlaceCreatedOverride ] = useState<LinkableValue>(undefined)
  const [ dateCreatedOverride, setDateCreatedOverride ] = useState<LinkableValue>(undefined)
  const [ materialsOverride, setMaterialsOverride ] = useState<LinkableValue>(undefined)
  const [ creditLineOverride, setCreditLineOverride ] = useState<LinkableValue>(undefined)
  const [ imageOverride, setImageOverride ] = useState<LinkableValue>(undefined)
  const [ imageCopyrightOverride, setImageCopyrightOverride ] = useState<LinkableValue>(undefined)
  const [ objectDescriptionOverride, setObjectDescriptionOverride ] = useState<LinkableValue>(undefined)
  const [ objectCopyrightOverride, setObjectCopyrightOverride ] = useState<LinkableValue>(undefined)

  const [ placementOverride, setPlacementOverride ] = useState<LinkableValue>(undefined)
  const [placementOverrideFieldValues, setPlacementOverrideFieldValues] = useState<
    { label: string; value: LinkableValue }[]
  >([]);

  const [ addToToursStop, setAddToToursStop ] = useState<Stop.Entity>();
  const [ deleteStopModalOpen, setDeleteStopModalOpen ] = useState(false)
  const [siteId, setSiteId] = useState('');

  // where to return to if the user wants to navigate back.
  // if we came here from within a tour, go back to that tour, otherwise go to all stops.
  const backLocation = tourId ? `/project/${projectId}/tour/${tourId}` : `/project/${projectId}/stops`

  const stopData = statusSelector.data(stopUpdate)
  const validationFailuresListData = statusSelector.data(validationFailuresList)
  const spacesListData = statusSelector.data(spacesList)
  const stop = stopData ? stopData.stop : undefined
  const tours = stopData ? stopData.tours : undefined
  const moods = stopData ? stopData.moods : undefined
  const isBasedOn = stop ? !!stop.basedOnRef : undefined
  const tmsObject = stopData ? stopData.baseEntity : undefined
  const fieldMappings = stopData ? stopData.fieldMappings : undefined
  const resolvedValues = stopData ? stopData.resolvedValues : undefined
  const sitesData = statusSelector.data(sites)
  const stopRef: Stop.Ref = {
    typename: Stop.refName,
    id: stopId,
  }

  const deepLinkUrl = (() => {
    if (typeof tourId === 'undefined') {
      return `${baseDeepLinkDomain}?deeplink=site/${siteId}/stop/${stopId}`
    } else {
      return `${baseDeepLinkDomain}?deeplink=site/${siteId}/tour/${tourId}/tour-stop/${stopId}`
    }
  })()

  // load initial stop data.
  useEffect(() => {
    if (stopUpdate.status === Status.Idle || (stop && Stop.mustRef(stop.ref).id !== stopId)) {
      gettyGetStopRequest({
        type: GetStopRequest.refName,
        stopRef: stopRef
      })
      gettyListSitesRequest()
      Stop.refName
      hasSubmitted.current = false
    }

    if (spacesList.status === Status.Idle) {
      spacesRequest()
    }

    releaseControlsListReleaseControlsRequest([stopRef])

    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [stopRef]
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  
  const getResolvedFormValues = (keys: ReadonlyArray<keyof Stop.Entity>): { [key: string]: any} => {
    const stopValues: Partial<Stop.Entity> = pick({
      ...Stop.defaults,
      ...stop
    }, ...keys)
    return convertEntityToFormValues<Stop.Entity>(stopValues, stopSchema, resolvedValues as any)
  }
  
  // resolves the site name based on the ref passed in.
  const resolveSiteName = useCallback((siteRef: Site.Ref | undefined) => {
    if (!siteRef) {
      return ""
    }
    
    const site = sitesData && sitesData.sites && sitesData.sites.find((site) => Site.mustRef(site.ref).id === siteRef.id)
    return site ? site.name : ""
  },[sitesData]);

  useEffect(() => {
    if (!spacesListData) return;

    const formatted = spacesListData.map(item => {
      const value: Placement.Entity = {
        type: 'mos.placement.Placement',
        placement: {
          type: 'mos.placement.SpacePlacement',
          spaceRef: Space.mustRef(item.ref)
        }
      };

      const label = `${resolveSiteName(item.siteRef)} - ${
        item.displayName ? item.displayName : item.name
      }`;

      return { label, value };
    });

    setPlacementOverrideFieldValues(formatted);
    
  }, [spacesListData, resolveSiteName])
  
  // setup the initial form values.
  const initialFormValues: FormValues | undefined = stop && Stop.mustRef(stop.ref).id === stopId ?
    {
      ...(getResolvedFormValues(['title', 'artworkName', 'producedBy', 'placeCreated', 'dateCreated', 'materials', 'creditLine', 'imageAltText', 'imageCopyright', 'wallNumber', 'objectCopyright', 'objectDescription', 'publicImageTitle']) as FormValues),
      placement: resolvedValues
        ? getSpacePlacementSpaceRef(getResolvedValue(stopSchema.placement.read(stop.placement), resolvedValues) as Placement.Entity)
        : getSpacePlacementSpaceRef(stopSchema.placement.read(stop.placement) as Placement.Entity)
    }:
    undefined
  const initialImageValue = stop && getResolvedFormValues(['image']).image as Media.Ref | undefined
  const initialAudioValue = stop && stopSchema.audio.read(stop.audio) as Media.Ref | undefined

  useEffect(() => {
    if (initialFormValues && initialFormValues.placement) {
      const space = spacesListData && spacesListData.find(obj => obj && obj.ref && initialFormValues && initialFormValues.placement && obj.ref.id === initialFormValues.placement.id);
  
      if (space && space.siteRef && space.siteRef.id) {
        setSiteId(space.siteRef.id)

      }
    }
  }, [initialFormValues]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // once the entity is ready, initialise the form values, or reinitialise once an update was
    // successful.
    if (stopUpdate.status === Status.Ready && initialFormValues) {
      setFormBag(createFormBag(initialFormValues))
      setImageMedia(initialImageValue)
      setAudioMedia(initialAudioValue)
    }

    // if the status is ready and the data had been submitted, trigger success toast.
    if (stopUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([stopRef])
      toastNotification({ type: 'success', text: 'Stop updated' })
      hasSubmitted.current = false
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: [stopRef]
        }
      })
    }

    // if the status is stale, trigger failure toast.
    if (stopUpdate.status === Status.Stale) {
      gettyUpdateStopReset()
      toastNotification({ type: 'error', text: 'Can’t save because of an unknown error.' })
      hasSubmitted.current = false
    }
  }, [ stopUpdate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  // handle the form ui.
  const modified = initialFormValues && stopData && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialImageValue, imageMedia) ||
    !areRefValuesEqual(initialAudioValue, audioMedia) ||
    // check if linkable override values contain data.
    !!titleOverride && compareValueInequality(stopData.resolvedValues, titleOverride, formBag.values.title) ||
    !!artworkNameOverride && compareValueInequality(stopData.resolvedValues, artworkNameOverride, formBag.values.artworkName) ||
    !!producedByOverride && compareValueInequality(stopData.resolvedValues, producedByOverride, formBag.values.producedBy) ||
    !!placeCreatedOverride && compareValueInequality(stopData.resolvedValues, placeCreatedOverride, formBag.values.placeCreated) ||
    !!dateCreatedOverride && compareValueInequality(stopData.resolvedValues, dateCreatedOverride, formBag.values.dateCreated) ||
    !!materialsOverride && compareValueInequality(stopData.resolvedValues, materialsOverride, formBag.values.materials) ||
    !!creditLineOverride && compareValueInequality(stopData.resolvedValues, creditLineOverride, formBag.values.creditLine) ||
    !!imageOverride && !areRefValuesEqual(initialImageValue, imageOverride as Media.Ref) ||
    !!imageCopyrightOverride && compareValueInequality(stopData.resolvedValues, imageCopyrightOverride, formBag.values.imageCopyright) ||
    !!objectDescriptionOverride && compareValueInequality(stopData.resolvedValues, objectDescriptionOverride, formBag.values.objectDescription) ||
    !!objectCopyrightOverride && compareValueInequality(stopData.resolvedValues, objectCopyrightOverride, formBag.values.objectCopyright) ||
    !!placementOverride && compareValueInequality(stopData.resolvedValues, placementOverride, formBag.values.placement)
  )

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}

    // title field validation.
    if (
      // no form title data, when there is no linkable override in place.
      ((!values.title || !values.title.trim().length) && !titleOverride) ||
      // empty string for custom title linkable override value.
      (!!titleOverride && typeof titleOverride === 'string' && !titleOverride.trim().length)
    ) {
      errors.title = 'Title required'
    }

    return errors
  }

  // attempt to submit validated data to the api.
  const submit = (formBag: FormBag<FormValues>, stop: Stop.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if (!bag.valid) {
      return false
    }

    hasSubmitted.current = true
    const values: Omit<FormValues, "placement"> = formBag.values;
    const entityValues = Object.entries(values).reduce((accumulator, currentValue) => {
      const key = currentValue[0] as keyof Stop.Entity
      const value = currentValue[1]
      if (key === "placement") return accumulator
      return {
        ...accumulator,
        [key]: stopSchema[key] ? stopSchema[key].write(value, stop[key]) : value
      }
    }, {})

    const placement = formBag.values.placement && makePlacementEntity(formBag.values.placement);

    gettyUpdateStopRequest({
      ...UpdateStopRequest.defaults,
      stopRef: Stop.mustRef(stop.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          // if values could be linked, only add to update mask if a new override has been set.
          // otherwise ignore the value because we have mutated the attribute ref based values to be
          // literal strings and media refs.
          ...(isBasedOn && !titleOverride ? [] : ['title']),
          ...(isBasedOn && !artworkNameOverride ? [] : ['artworkName']),
          ...(isBasedOn && !producedByOverride ? [] : ['producedBy']),
          ...(isBasedOn && !placeCreatedOverride ? [] : ['placeCreated']),
          ...(isBasedOn && !dateCreatedOverride ? [] : ['dateCreated']),
          ...(isBasedOn && !materialsOverride ? [] : ['materials']),
          ...(isBasedOn && !creditLineOverride ? [] : ['creditLine']),
          ...(isBasedOn && !imageOverride ? [] : ['image']),
          'imageAltText',
          ...(isBasedOn && !imageCopyrightOverride ? [] : ['imageCopyright']),
          'wallNumber',
          ...(isBasedOn && !placementOverride ? [] : ["placement"]),
          ...(isBasedOn && !objectDescriptionOverride ? [] : ['objectDescription']),
          ...(isBasedOn && !objectCopyrightOverride ? [] : ['objectCopyright']),
          'audio',
          'publicImageTitle',
        ] as any,
      },
      ...entityValues,
      placement: writeUsingSchema<Stop.Entity>(stopSchema, "placement", placement, stop.placement),
      image: writeUsingSchema<Stop.Entity>(stopSchema, 'image', imageMedia, stop.image),
      audio: writeUsingSchema<Stop.Entity>(stopSchema, 'audio', audioMedia, stop.audio),

      // spread in linkable override values if they have been set, otherwise previous value remains.
      ...(isBasedOn && !!titleOverride ?
        { title: writeUsingSchema<Stop.Entity>(stopSchema, 'title', titleOverride, stop.title) } :
        null
      ),
      ...(isBasedOn && !!artworkNameOverride ?
        { artworkName: writeUsingSchema<Stop.Entity>(stopSchema, 'artworkName', artworkNameOverride, stop.artworkName) } :
        null
      ),
      ...(isBasedOn && !!producedByOverride ?
        { producedBy: writeUsingSchema<Stop.Entity>(stopSchema, 'producedBy', producedByOverride, stop.producedBy) } :
        null
      ),
      ...(isBasedOn && !!placeCreatedOverride ?
        { placeCreated: writeUsingSchema<Stop.Entity>(stopSchema, 'placeCreated', placeCreatedOverride, stop.placeCreated) } :
        null
      ),
      ...(isBasedOn && !!dateCreatedOverride ?
        { dateCreated: writeUsingSchema<Stop.Entity>(stopSchema, 'dateCreated', dateCreatedOverride, stop.dateCreated) } :
        null
      ),
      ...(isBasedOn && !!materialsOverride ?
        { materials: writeUsingSchema<Stop.Entity>(stopSchema, 'materials', materialsOverride, stop.materials) } :
        null
      ),
      ...(isBasedOn && !!creditLineOverride ?
        { creditLine: writeUsingSchema<Stop.Entity>(stopSchema, 'creditLine', creditLineOverride, stop.creditLine) } :
        null
      ),
      ...(isBasedOn && !!imageOverride ?
        { image: writeUsingSchema<Stop.Entity>(stopSchema, 'image', imageOverride, stop.image) } :
        null
      ),
      ...(isBasedOn && !!imageCopyrightOverride ?
        { imageCopyright: writeUsingSchema<Stop.Entity>(stopSchema, 'imageCopyright', imageCopyrightOverride, stop.imageCopyright) } :
        null
      ),
      ...(isBasedOn && !!objectDescriptionOverride ?
        { objectDescription: writeUsingSchema<Stop.Entity>(stopSchema, 'objectDescription', objectDescriptionOverride, stop.objectDescription) } :
        null
      ),
      ...(isBasedOn && !!objectCopyrightOverride ?
        { objectCopyright: writeUsingSchema<Stop.Entity>(stopSchema, 'objectCopyright', objectCopyrightOverride, stop.objectCopyright) } :
        null
      ),
      ...(isBasedOn && !!placementOverride ?
        { placement: writeUsingSchema<Stop.Entity>(stopSchema, "placement", placementOverride, stop.placement) } :
        null
      ),
    })

    return false
  }

  const placementOption = (value: ResolvedValueResult.Value.Entity | ResolvedValueResult.Error.Entity | undefined) => {
    const resolved = spacesListData && spacesListData.find((item: Space.Entity) => {
      const id = Space.mustRef(item.ref).id
      return value &&
        value.type === ResolvedValueResult.Value.refName &&
        value.targetValue &&
        value.targetValue.type === ResolvedValueResult.Value.PlacementValue.refName &&
        value.targetValue.placement &&
        value.targetValue.placement.type === Placement.refName &&
        value.targetValue.placement.placement &&
        value.targetValue.placement.placement.type === SpacePlacement.refName &&
        value.targetValue.placement.placement.spaceRef &&
        id === value.targetValue.placement.placement.spaceRef.id
    })
    return resolved ? `${resolveSiteName(resolved.siteRef)} - ${resolved.displayName ? resolved.displayName : resolved.name}` : ''
  }

  const placementAlternativeFieldValues = fieldMappings && resolvedValues && getAlternateFieldValues<Stop.Entity>("placement", fieldMappings, resolvedValues, placementOption)

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: stopRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === stopId) : undefined
  const failureFields = failures && failures.issues
  const missingTmsObject = failureFields && failureFields.find(field => field.issueType === "MISSING_TMS_OBJECT");

  const isOverridden = (stop: any, stopField: string, defaultField: string): boolean => {
    if (typeof stop.basedOnRef === 'undefined') {
      return false;
    }

    if (stopField === "imageCopyright" || stopField === "objectCopyright" || stopField === "image" || stopField === "placement"){
      if (stop && stop[stopField] &&
        stop[stopField].value &&
        stop[stopField].value.attributeName !== defaultField) {
        return true
      }
      return false
    }

    const enUsTranslation = (stop as any) &&
      (stop as any)[stopField] &&
      (stop as any)[stopField].translatedValues &&
      (stop as any)[stopField].translatedValues.find(
        (tv: any) => tv.language &&
        tv.language.languageCode === 'en' && 
        tv.language.variant === 'US'
      );

    const customValue = enUsTranslation &&
      enUsTranslation.linkableStringValue &&
      (enUsTranslation.linkableStringValue.stringValue ||
      (typeof enUsTranslation.linkableStringValue.value === 'string' &&
        enUsTranslation.linkableStringValue.value)) ||
      undefined;

    const alternateValue = enUsTranslation &&
      enUsTranslation.linkableStringValue && (
        (enUsTranslation.linkableStringValue.linkTargetFieldRef &&
          enUsTranslation.linkableStringValue.linkTargetFieldRef.attributeName) ||
        (enUsTranslation.linkableStringValue.value &&
          enUsTranslation.linkableStringValue.value.attributeName) ||
        undefined
      );

    if (customValue || alternateValue && alternateValue !== defaultField){
      return true;
    }

    return false
  }

  const getDefaultValue = (stop: any, stopField: string, defaultField: string): any => {

    if (typeof stop.basedOnRef === 'undefined') {
      return false;
    }

    if (stopField === "imageCopyright" || stopField === "objectCopyright" || stopField === "image" || stopField === "placement"){
      return stop && stop[stopField] && stop[stopField].value
    }

    const enUsTranslation = (stop as any) &&
      (stop as any)[stopField] &&
      (stop as any)[stopField].translatedValues &&
      (stop as any)[stopField].translatedValues.find(
        (tv: any) => tv.language &&
        tv.language.languageCode === 'en' &&
        tv.language.variant === 'US'
      );

    const customValue = enUsTranslation &&
      enUsTranslation.linkableStringValue &&
      (enUsTranslation.linkableStringValue.stringValue ||
      (typeof enUsTranslation.linkableStringValue.value === 'string' &&
        enUsTranslation.linkableStringValue.value)) ||
      undefined;

    const alternateValue = enUsTranslation && 
      enUsTranslation.linkableStringValue && (
        enUsTranslation.linkableStringValue.linkTargetFieldRef ||
        enUsTranslation.linkableStringValue.value ||
        undefined
      );
    
    return customValue || alternateValue
  }

  return (
    <>
      {addToToursStop && (
        <StopUpdateTours
          stop={addToToursStop}
          onCancel={() => setAddToToursStop(undefined)}
          onComplete={() => setAddToToursStop(undefined)}
        />
      )}
      {deleteStopModalOpen && (
        <StopDelete
          historyPush={historyPush}
          projectId={projectId}
          onCancel={() => setDeleteStopModalOpen(false)}
          stop={stop}
          tours={tours}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Stop"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={initialFormValues && initialFormValues.title || ''}
        toolbar={() => (
          <ButtonGroup>
            <ReleaseControlUpdate
              hasBorder
              entityRef={stopRef}
              updateHandler={releaseControlUpdate}
            />
            <PopOverMenu hasBorder>
              <PopOverMenuButton onClick={() => setAddToToursStop(stop)}>
                Add to Tour
              </PopOverMenuButton>
              <PopOverMenuButton onClick={() => setDeleteStopModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}  
        summary={failureFields ? (
          // eslint-disable-next-line react/display-name
          () => (
            <StatusSummary 
              warning={[{
                text: missingTmsObject
                  ? "This stop has an unlinked TMS object"
                  : `The following fields are required to publish this stop: ${
                      failureFields
                        .map(({ attributeDisplayName }) => attributeDisplayName)
                        .join(', ')
                    }.`
              }]}
            />
          )
        ) : undefined}
        subnav={() => (
          <SecondaryNavigation
            navItems={getStopSubnav(projectId, stopId, tourId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        content={() => (
          <StatusGuard status={spacesList}>
            <StatusGuard status={stopUpdate} mode="form">
              {!formBag && !stop && missingTmsObject && (
                <>
                  <h4>Missing TMS Object</h4>
                  {missingTmsObject.attributeDisplayName.split(',').map((item, index) => (
                    <React.Fragment key={index}>
                      {item.trim()}
                      <br />
                    </React.Fragment>
                  ))}
                </>
              )}
              {formBag && spacesListData && stop && (
                <>
                  <form noValidate onSubmit={(event) => { event.preventDefault() }}>
                    <FormData
                      bag={formBag}
                      onUpdate={(event) => {
                        setFormBag(event.bag)
                      }}
                      validate={() => ({})}
                    >
                      <Fieldset legend="Details">
                        <Input
                          label="Stop Title"
                          field="title"
                          error={formBag.touched.title ? formBag.errors.title : undefined}
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'title',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'title', 'primary_title'),
                              onUpdate: value => setTitleOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('title', failureFields)}
                          isOverridden={isOverridden(stop, 'title', 'primary_title')}
                        />
                        <Input
                          label="Artwork Title"
                          field="artworkName"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'artworkName',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'artworkName', 'primary_title'),
                              onUpdate: value => setArtworkNameOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('artworkName', failureFields)}
                          isOverridden={isOverridden(stop, 'artworkName', 'primary_title')}
                        />
                        <Input
                          label="Produced by"
                          field="producedBy"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'producedBy',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'producedBy', 'produced_by'),
                              onUpdate: value => setProducedByOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('producedBy', failureFields)}
                          isOverridden={isOverridden(stop, 'producedBy', 'produced_by')}
                        />
                        <Input
                          label="Place Created"
                          field="placeCreated"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'placeCreated',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'placeCreated', 'place_created'),
                              onUpdate: value => setPlaceCreatedOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('placeCreated', failureFields)}
                          isOverridden={isOverridden(stop, 'placeCreated', 'place_created')}
                        />
                        <Input
                          label="Date Created"
                          field="dateCreated"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'dateCreated',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'dateCreated', 'date_created'),
                              onUpdate: value => setDateCreatedOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('dateCreated', failureFields)}
                          isOverridden={isOverridden(stop, 'dateCreated', 'date_created')}
                        />
                        <Input
                          label="Materials"
                          field="materials"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'materials',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'materials', 'materials'),
                              onUpdate: value => setMaterialsOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('materials', failureFields)}
                          isOverridden={isOverridden(stop, 'materials', 'materials')}
                        />
                        <Input
                          label="Credit Line"
                          field="creditLine"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'creditLine',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'creditLine', 'credit_line'),
                              onUpdate: value => setCreditLineOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('creditLine', failureFields)}
                          isOverridden={isOverridden(stop, 'creditLine', 'credit_line')}
                        />
                        <FieldSection />
                        <MediaField
                          kind="MEDIA_KIND_RASTER_IMAGE"
                          mediaRef={imageMedia}
                          onUpdate={(entity) => { setImageMedia(entity ? entity.ref : undefined) }}
                          label="Hero Image"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'image',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'image', 'image_media_ref'),
                              onUpdate: value => setImageOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('image', failureFields)}
                          isOverridden={isOverridden(stop, 'image', 'image_media_ref')}
                        />
                        <Input
                          label="Alternate Text"
                          description="Description of what the image contains if it cannot be shown."
                          field="imageAltText"
                          isAssociated={true}
                          warning={getValidationWarning<Stop.Entity>('imageAltText', failureFields)}
                        />
                        <Input
                          label="Public Image Title"
                          description="Title of the image."
                          field="publicImageTitle"
                          isAssociated={true}
                          warning={getValidationWarning<Stop.Entity>('publicImageTitle', failureFields)}
                        />
                        <Input
                          label="Image Copyright"
                          description="Appended to the image caption"
                          field="imageCopyright"
                          isAssociated={true}
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'imageCopyright',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'imageCopyright', 'image_copyright'),
                              onUpdate: value => setImageCopyrightOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('imageCopyright', failureFields)}
                          isOverridden={isOverridden(stop, 'imageCopyright', 'image_copyright')}
                        />
                        <FieldSection />
                        <div style={{ paddingRight: '50%' }}>
                          <Input label="Wall number" field="wallNumber" />
                        </div>
                        <SelectRef<FormValues, "placement">
                          label="Location"
                          field="placement"
                          isClearable
                          options={spacesListData.map((item) => ({
                            label: `${resolveSiteName(item.siteRef)} - ${item.displayName ? item.displayName : item.name}`,
                            value: Space.mustRef(item.ref)
                          }))}
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues &&
                            placementAlternativeFieldValues && placementAlternativeFieldValues.length ? {
                                allowCustomOverride: false,
                                alternateFieldValues: placementAlternativeFieldValues,
                                customOverrideValues: placementOverrideFieldValues,
                                defaultValue: getDefaultValue(stopData && stopData.stop, 'placement', 'placement'),
                                onUpdate: (value) => setPlacementOverride(value)
                              }
                            : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('placement', failureFields)}
                          isOverridden={isOverridden(stop, 'placement', 'placement')}
                        />
                        <FieldSection />
                        <Input
                          label="Description"
                          field="objectDescription"
                          component="textarea"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'objectDescription',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'objectDescription', 'object_description'),
                              onUpdate: value => setObjectDescriptionOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('objectDescription', failureFields)}
                          isOverridden={isOverridden(stop, 'objectDescription', 'object_description')}
                        />
                        <Input
                          label="Object Copyright"
                          field="objectCopyright"
                          linkableOptions={isBasedOn && fieldMappings && resolvedValues ? {
                              alternateFieldValues: getAlternateFieldValues<Stop.Entity>(
                                'objectCopyright',
                                fieldMappings,
                                resolvedValues
                              ),
                              defaultValue: getDefaultValue(stopData && stopData.stop, 'objectCopyright', 'object_copyright'),
                              onUpdate: value => setObjectCopyrightOverride(value)
                            } : undefined
                          }
                          warning={getValidationWarning<Stop.Entity>('objectCopyright', failureFields)}
                          isOverridden={isOverridden(stop, 'objectCopyright', 'object_copyright')}
                        />
                      </Fieldset>
                      <Fieldset legend="Audio">
                        <MediaField
                          kind="MEDIA_KIND_AUDIO"
                          mediaRef={audioMedia}
                          onUpdate={(entity) => { setAudioMedia(entity ? entity.ref : undefined) }}
                          label="Audio"
                          warning={getValidationWarning<Stop.Entity>('audio', failureFields)}
                        />
                        {!audioMedia &&
                          <Button variant="link" onClick={() => {
                            return setAudioMedia(Media.mustRef({
                              typename: 'mos.media.Media',
                              id: placeholderAudioRefId
                            }));
                          }}>Add placeholder audio: Only for use with Mood Journeys</Button>
                        }
                      </Fieldset>
                    </FormData>
                  </form>
                  <>
                    <h4>Deep Link</h4>
                    {siteId !== '' ? (
                      <a href={deepLinkUrl} target="_blank" rel="noopener noreferrer">
                        {deepLinkUrl}
                      </a>
                      ) : (
                        "To generate a deep link please select a location"
                      )
                    }
                  </>
                </>
              )}
            </StatusGuard>
          </StatusGuard>
        )}
        sidebar={() => (
          <TmsDetails
            tmsObject={tmsObject}
            sites={sites}
            spacesListData={spacesListData}
            stop={stop}
            tours={tours}
            moods={moods}
          />
        )}
        footer={() => formBag && stop && (
          <FooterEditEntity
            entity={stopUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBag, stop)
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const StopUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, "stopUpdate", "sites", "validationFailuresList"),
    ...pick(store.spaces, "spacesList"),
  }),
  {
    ...pick(actionCreators, 'gettyGetStopRequest', 'gettyUpdateStopRequest', 'gettyUpdateStopReset', 'gettyListSitesRequest', 'gettyListValidationFailuresRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(spacesBindableActionCreators, "spacesRequest"),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(StopUpdateView)
